import { FC, ReactNode, useState } from 'react'
import {
	Button,
	Card,
	Col,
	Divider,
	message,
	Row,
	Spin,
	Typography,
} from 'antd'
import {
	RiCheckDoubleFill,
	RiInformationLine,
	RiMessageFill,
} from 'react-icons/ri'

import useSingleBroadcast from 'hooks/useSingleBroadcast'
import getBroadcastReportExport, {
	BroadcastReportTypes,
} from 'api/broadcasts/getBroadcastReportExport'
import downloadUsingAPI from 'utils/downloadUsingAPI'
import toTileCase from 'utils/toTitleCase'
import handleAxiosError from 'utils/handleAxiosError'
import styles from './styles.module.scss'

const { Text } = Typography

interface CardDataType {
	title: string
	stats: number
	icon: ReactNode
	downloadType: BroadcastReportTypes
	loader: boolean
}

const BroadcastDownloads: FC = () => {
	const { broadcast, loading, insights } = useSingleBroadcast()

	const [generatingReport, setGeneratingReport] = useState({
		ALL: false,
		SENT: false,
		READ: false,
		DELIVERED: false,
		UNDELIVERED: false,
		FAILED: false,
		REPLY: false,
	})

	const validateBroadcast = () => {
		if (broadcast === null) {
			throw new Error('Broadcast is null')
		}
	}

	const downloadReport = async (type: BroadcastReportTypes) => {
		try {
			setGeneratingReport(state => ({ ...state, [type]: true }))

			validateBroadcast()

			const options = {
				id: broadcast!._id,
				clientId: broadcast!.client._id,
				type,
			}

			await downloadUsingAPI(
				await getBroadcastReportExport(options),
				`${broadcast!.name}-${type}-REPORT.csv`
			)

			message.success(
				`Broadcast: ${broadcast!.name}, ${toTileCase(
					type
				)} report has been downloaded.`
			)

			setGeneratingReport(state => ({ ...state, [type]: false }))
		} catch (error: any) {
			setGeneratingReport(state => ({ ...state, [type]: false }))
			handleAxiosError(error)
		}
	}

	const cardsData: Array<CardDataType> = [
		{
			icon: <RiMessageFill fill='#17a2b8' size={15} />,
			title: 'Replies',
			stats: insights?.replies ?? 0,
			downloadType: 'REPLY',
			loader: generatingReport.REPLY,
		},
		{
			icon: <RiCheckDoubleFill fill='#aaa' size={15} />,
			title: 'Delivered',
			stats: insights?.deliveredCount ?? 0,
			downloadType: 'DELIVERED',
			loader: generatingReport.DELIVERED,
		},
		{
			icon: <RiInformationLine fill='red' size={15} />,
			title: 'Undelivered',
			stats: insights?.failedCount ?? 0,
			downloadType: 'UNDELIVERED',
			loader: generatingReport.UNDELIVERED,
		},
		{
			icon: <RiCheckDoubleFill fill='#9D1F5D' size={15} />,
			title: 'Read',
			stats: insights?.readCount ?? 0,
			downloadType: 'READ',
			loader: generatingReport.READ,
		},
	]

	return (
		<Spin spinning={loading} tip='Loading Reports'>
			<Row justify='center' gutter={12}>
				{cardsData.map(card => (
					<Col span={6}>
						<Card className={styles.downloads__Tab}>
							<Row align='middle' justify='space-between'>
								<Text className={styles.downloads__Text}>
									{card.icon}
									{card.title}
								</Text>
								<Text>{card.stats ?? 0}</Text>
							</Row>
							<Divider />
							<Button
								className={styles.downloads__Button}
								type='primary'
								onClick={() => downloadReport(card.downloadType)}
							>
								Download Reports
							</Button>
						</Card>
					</Col>
				))}
			</Row>
			<Button
				type='primary'
				className={styles.downloads__Button__All}
				onClick={() => downloadReport('ALL')}
			>
				Download All Reports
			</Button>
		</Spin>
	)
}

export default BroadcastDownloads
